'use client';
import { useState } from 'react';
import { FileText, Download, ImageOff } from 'lucide-react';
import { useTranslation } from '../../lib/i18n';
import type { BrainMessage, BrainMessageFile, BrainMessageImage } from '../../lib/types';
import { ImageLightbox } from '../../components/ui/ImageLightbox';
import { Badge } from '../../components/ui/Badge';
import { Button, buttonClassName } from '../../components/ui/Button';
import { formatBytes } from '../../lib/format';
import { Attachment, AttachmentActions, AttachmentContent, AttachmentDescription, AttachmentMedia, AttachmentTitle } from '../../components/ui/shadcn/attachment';

/** The pictures a turn carries. The daemon kept the files next to its database, so these render
 *  identically from the live stream and from reloaded history — the whole point of storing them. The
 *  `<img>` hits the same-origin proxy, which turns the session cookie into the daemon bearer, so no
 *  signed link is involved. Clicking one opens the picture full size, in the conversation. */
export function Attachments({ images, attached }: { images: BrainMessageImage[]; attached?: boolean }) {
  const { t } = useTranslation();
  // Whose picture it is, said in the description the picture carries: "Attached image" is only true of a
  // turn this reader sent, and a shared room shows everybody's pictures through this one control.
  const description = attached ? t.brainChat.attachmentAlt : t.brainChat.imageAttached;
  return (
    <div className="chat-attachments flex flex-wrap gap-2 my-1.5">
      {images.map((image) => <AttachmentThumb key={image.url} image={image} description={description} />)}
    </div>
  );
}

/** The files the sender attached in the composer, by the name they chose: a quiet chip each, with no link,
 *  because the message only records which files went with it, not an address anyone may fetch. It shows the
 *  same from the live stream and from reloaded history, and alone when the message has no text. */
export function ComposerFiles({ files }: { files: NonNullable<BrainMessage['attachments']> }) {
  return (
    <div className="flex flex-wrap gap-1.5 my-1.5">
      {files.map((file, index) => (
        <Badge key={`${index}:${file.relative}`} tone="muted" className="min-w-0 max-w-full gap-1" title={file.name}>
          <FileText size={12} aria-hidden />
          <span className="min-w-0 truncate">{file.name}</span>
        </Badge>
      ))}
    </div>
  );
}

/** One attachment, with an answer for the case where the bytes are gone. A stored picture can stop
 *  resolving — the daily sweep reclaims files nothing references any more, and a transcript opened
 *  read-only asks for an image the daemon will only serve to its owner (a 404 by design, so the request
 *  cannot be used to probe what other conversations hold). The browser's own reply to that is a broken
 *  image glyph, which reads as "the chat is buggy" rather than "this picture is no longer here". State it
 *  instead, and drop the click-through: a surface that can only fail to open is worse than no control. */
function AttachmentThumb({ image, description }: { image: BrainMessageImage; description: string }) {
  const { t } = useTranslation();
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(false);

  if (failed) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-border px-2.5 py-2 text-meta text-muted-foreground">
        <ImageOff size={14} className="shrink-0" aria-hidden />
        {t.brainChat.attachmentGone}
      </span>
    );
  }
  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => setOpen(true)}
        title={description}
        aria-label={t.brainChat.attachmentOpen}
        // The width cap sits on the FRAME, not the picture. A percentage max-width on the <img> is ignored
        // while the flex item measures its content, so a wide picture capped only by height left the
        // border hanging around empty space — the frame was sized for the uncapped width.
        className="h-auto max-w-[min(--spacing(64),100%)] overflow-hidden rounded-lg border-border bg-image-ground p-0 hover:border-primary hover:bg-transparent focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      >
        <img
          src={`/api${image.url}`}
          alt={description}
          onError={() => setFailed(true)}
          className="block max-h-48 max-w-full object-contain"
        />
      </Button>
      {open ? <ImageLightbox src={`/api${image.url}`} description={description} onClose={() => setOpen(false)} /> : null}
    </>
  );
}

/** An image the agent shared on purpose (`ShareImage`). It is the same picture-in-the-conversation the
 *  user's own attachments are — same proxy path, same click-through to full size — so it reuses
 *  {@link Attachments} rather than growing a second thumbnail. Only the caption underneath is its own. */
export function SharedImage({ image, caption }: { image: BrainMessageImage; caption?: string }) {
  return (
    <div className="flex min-w-0 flex-col">
      <Attachments images={[image]} />
      {caption ? <div className="mt-1 text-meta text-muted-foreground">{caption}</div> : null}
    </div>
  );
}

/** A file the agent handed over via ShareFile. The filename and size explain what will be fetched, while the
 *  shared button styling makes the action unmistakable without inventing a one-off control. */
export function SharedFile({ file, caption }: { file: BrainMessageFile; caption?: string }) {
  const { t, locale } = useTranslation();
  return (
    <Attachment state="done" size="sm" className="my-1.5 w-full">
        <AttachmentMedia><FileText aria-hidden /></AttachmentMedia>
        <AttachmentContent>
          <AttachmentTitle title={file.name}>{file.name}</AttachmentTitle>
          <AttachmentDescription>{formatBytes(file.size, locale)}{caption ? ` · ${caption}` : ''}</AttachmentDescription>
        </AttachmentContent>
        <AttachmentActions>
        <a
          href={`/api${file.url}`}
          download={file.name}
          className={buttonClassName('default', 'sm', 'shrink-0')}
          aria-label={`${t.brainChat.fileDownload}: ${file.name}`}
        >
          <Download size={14} aria-hidden />
          {t.brainChat.fileDownload}
        </a>
        </AttachmentActions>
    </Attachment>
  );
}
